import { useEffect, useState } from 'react';

import {
  useLazyGetPresignedURLQuery,
  useUploadDataFileMutation
} from '@api/projects';

const useUploadProject = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [uploadPercent, setUploadPercent] = useState({ loaded: 0, total: 0 });
  const [uploadSpreadsheet, setUploadSpreadsheet] = useState<boolean>(false);

  const [uploadDataFile, { isSuccess, isError: isDataFileError }] =
    useUploadDataFileMutation();

  useEffect(() => {
    isDataFileError && setIsError(true);
  });

  const [getSignedURLApi] = useLazyGetPresignedURLQuery();

  const getPreSingedURL = async (file_name: string, size: number) => {
    const response = await getSignedURLApi({ file_name, size });

    return response.data;
  };

  const uploadFileToGCP = (file: File, uploadURL: string) =>
    new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      xhr.open('PUT', uploadURL, true);

      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable)
          setUploadPercent({
            loaded: event.loaded,
            total: event.total
          });
      };

      xhr.onload = () => {
        if (xhr.status === 200) resolve(xhr);
        else reject(new Error('File upload failed.'));
      };

      xhr.send(file);
    });

  const userUploadProject = async (
    file: File,
    file_size: number,
    projectId?: string,
    sheetName?: string
  ) => {
    setIsLoading(true);
    const presignedURLRes = await getPreSingedURL(file.name, file.size);

    if (presignedURLRes?.signed_url)
      try {
        const uploadToBucketRes: XMLHttpRequest = (await uploadFileToGCP(
          file,
          presignedURLRes?.signed_url as string
        )) as XMLHttpRequest;

        if (uploadToBucketRes.status === 200) {
          const dataFileRes = await uploadDataFile({
            file_name: presignedURLRes?.file_name as string,
            project_id: projectId,
            sheet_name: sheetName
          }).unwrap();

          setTimeout(() => setIsLoading(false), 100);

          return dataFileRes;
        } else {
          setIsError(true);
        }
      } catch (err) {
        setIsError(true);

        return err;
      }
    else setIsError(true);
    setTimeout(() => setIsLoading(false), 100);
  };

  const userUploadSpreadsheet = async (uploadSpreadsheetPayload: {
    file_name: string;
    sheet_name: string;
    sheet_link?: string;
    sheet_id?: string;
    project_id?: string;
  }) => {
    setIsLoading(true);
    setUploadPercent({ loaded: 0, total: 0 });
    setUploadSpreadsheet(true);

    const dataFileRes = await uploadDataFile(uploadSpreadsheetPayload).unwrap();

    return dataFileRes;
  };

  useEffect(() => {
    if (uploadSpreadsheet) {
      setUploadPercent({ loaded: 60, total: 100 });
      setTimeout(() => {
        setUploadPercent({ loaded: 100, total: 100 });
      }, 100);
    }
  }, [isSuccess]);

  return {
    userUploadProject,
    userUploadSpreadsheet,
    isUploading: isLoading,
    isUploadSuccess: isSuccess,
    isUploadError: isError,
    uploadPercent,
    uploadSpreadsheet
  };
};

export default useUploadProject;
